import { useState } from "react";
import { CheckCircle2, ChevronDown, Loader2, Mail, MapPin, MessageCircle } from "lucide-react";
import { Eyebrow, Reveal, WHATSAPP_URL } from "./shared";

const INTERESTS = [
    "Employer — Hire Talent",
    "Professional — Find International Jobs",
    "Study Abroad",
    "Scholarships",
    "Visa Assistance",
    "Tourism Visa",
    "Hajj & Umrah",
    "Other",
];

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

export const Contact = () => {
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/contact-enquiries`,
                { method: "POST", body: new FormData(e.target) }
            );
            if (!res.ok) throw new Error("send failed");
            setStatus("success");
            document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
        } catch {
            setStatus("error");
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-ivory py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
                    <Reveal className="lg:col-span-5">
                        <Eyebrow>Contact</Eyebrow>
                        <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            Let's Start the Conversation
                        </h2>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-ink/65">
                            Tell us what you're looking for — our team in Nigeria and the United States will
                            respond with clear next steps.
                        </p>

                        <div className="mt-10 space-y-8">
                            <div className="flex gap-4">
                                <MapPin size={20} strokeWidth={1.5} className="mt-1 shrink-0 text-forest" />
                                <div className="text-sm leading-relaxed text-ink/70">
                                    <p className="font-medium text-ink">Afrinexa Global Ltd</p>
                                    <p>No. 44 University Road,</p>
                                    <p>Ilorin, Kwara State, Nigeria</p>
                                    <p className="mt-1 text-xs uppercase tracking-widest text-ink/45">
                                        CAC: RC 8710821
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <MapPin size={20} strokeWidth={1.5} className="mt-1 shrink-0 text-forest" />
                                <div className="text-sm leading-relaxed text-ink/70">
                                    <p className="font-medium text-ink">Afrinexa Global LLC</p>
                                    <p>150 E B St Lbby,</p>
                                    <p>Casper, WY 82601, USA</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <Mail size={20} strokeWidth={1.5} className="mt-1 shrink-0 text-forest" />
                                <div className="text-sm leading-relaxed">
                                    <a
                                        href="mailto:contact@afrinexaglobal.org"
                                        data-testid="contact-email-link"
                                        className="block text-ink/70 transition-colors hover:text-forest"
                                    >
                                        contact@afrinexaglobal.org
                                    </a>
                                    <a
                                        href="mailto:info@afrinexaglobal.org"
                                        data-testid="contact-email-info-link"
                                        className="block text-ink/70 transition-colors hover:text-forest"
                                    >
                                        info@afrinexaglobal.org
                                    </a>
                                </div>
                            </div>
                        </div>

                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-btn"
                            className="mt-10 inline-flex items-center gap-3 border border-forest/30 px-6 py-4 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-ivory"
                        >
                            <MessageCircle size={18} strokeWidth={1.5} />
                            WhatsApp Us — +234 814 063 5506
                        </a>
                    </Reveal>

                    <Reveal delay={0.15} className="lg:col-span-7">
                        <div className="border border-ink/10 bg-sand p-7 sm:p-10">
                            {status === "success" ? (
                                <div
                                    data-testid="contact-form-success"
                                    className="flex min-h-96 flex-col items-center justify-center text-center"
                                >
                                    <CheckCircle2 size={44} strokeWidth={1.25} className="text-forest" />
                                    <h3 className="mt-6 font-serif text-2xl font-medium tracking-tight text-ink">
                                        Thank you — your inquiry has been received.
                                    </h3>
                                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink/60">
                                        Our team will review your message and contact you using the details
                                        provided.
                                    </p>
                                    <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
                                        <button
                                            type="button"
                                            data-testid="contact-send-another-btn"
                                            onClick={() => setStatus("idle")}
                                            className="inline-flex items-center gap-2 bg-forest px-7 py-3.5 text-sm font-medium text-ivory transition-colors hover:bg-forest-deep"
                                        >
                                            Send Another Message
                                        </button>
                                        <a
                                            href={WHATSAPP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="success-whatsapp-btn"
                                            className="inline-flex items-center gap-2 border border-forest/30 px-7 py-3.5 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-ivory"
                                        >
                                            <MessageCircle size={16} strokeWidth={1.5} />
                                            Continue on WhatsApp
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <form data-testid="contact-form" onSubmit={handleSubmit}>
                                    <input
                                        type="text"
                                        name="fax"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        aria-hidden="true"
                                        className="absolute -left-[9999px] h-0 w-0 opacity-0"
                                    />
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="contact-name" className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                                                Name<span className="ml-1 text-champagne" aria-hidden="true">*</span>
                                            </label>
                                            <input id="contact-name" name="name" data-testid="contact-name-input" type="text" required placeholder="Your full name" className={inputClass} />
                                        </div>
                                        <div>
                                            <label htmlFor="contact-email" className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                                                Email<span className="ml-1 text-champagne" aria-hidden="true">*</span>
                                            </label>
                                            <input id="contact-email" name="email" data-testid="contact-email-input" type="email" required placeholder="you@example.com" className={inputClass} />
                                        </div>
                                        <div>
                                            <label htmlFor="contact-phone" className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                                                Phone / WhatsApp
                                            </label>
                                            <input id="contact-phone" name="phone" data-testid="contact-phone-input" type="tel" placeholder="+234 ..." className={inputClass} />
                                        </div>
                                        <div>
                                            <label htmlFor="contact-interest" className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                                                I am interested in<span className="ml-1 text-champagne" aria-hidden="true">*</span>
                                            </label>
                                            <div className="relative">
                                                <select id="contact-interest" name="interest" data-testid="contact-interest-select" required defaultValue="" className={`${inputClass} cursor-pointer appearance-none pr-10`}>
                                                    <option value="" disabled>Select a service</option>
                                                    {INTERESTS.map((interest) => (
                                                        <option key={interest} value={interest}>{interest}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={16} strokeWidth={1.5} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink/40" />
                                            </div>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="contact-message" className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                                                Message<span className="ml-1 text-champagne" aria-hidden="true">*</span>
                                            </label>
                                            <textarea id="contact-message" name="message" data-testid="contact-message-input" rows={5} required placeholder="Tell us a little about what you need…" className={`${inputClass} resize-none`} />
                                        </div>
                                    </div>

                                    {status === "error" && (
                                        <div
                                            data-testid="contact-form-error"
                                            className="mt-8 border border-ink/20 bg-ivory p-5 text-sm leading-relaxed text-ink/70"
                                        >
                                            Your message could not be sent right now. Please try again, or
                                            reach us directly on{" "}
                                            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-forest underline underline-offset-4">
                                                WhatsApp
                                            </a>{" "}
                                            or by{" "}
                                            <a href="mailto:contact@afrinexaglobal.org" className="font-medium text-forest underline underline-offset-4">
                                                email
                                            </a>
                                            .
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        data-testid="contact-submit-button"
                                        disabled={status === "submitting"}
                                        className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-ivory transition-colors duration-300 hover:bg-forest-deep disabled:cursor-not-allowed disabled:opacity-80 sm:w-auto"
                                    >
                                        {status === "submitting" ? (
                                            <>
                                                <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            "Send Inquiry"
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
